import { parseCodingNotes } from '../claim-details/claim-timeline/items/coding-detail.utils';
import { mapWithConcurrency } from '../../utils';

export interface CoderRuleOptionSource {
    ruleNumber?: number;
    description?: string;
    isActive?: boolean;
}

export interface CoderRuleSelectOption {
    value: string;
    label: string;
    ruleNumber: number;
}

export interface ClaimRulePair {
    claimId: string;
    ruleNumber: number;
}

export interface PairAttemptResult {
    pair: ClaimRulePair;
    ok: boolean;
    error?: string;
}

export interface SuppressedCoderRule {
    ruleNumber: number;
    description?: string;
}

export interface CoderRuleCodingSource {
    notes?: unknown;
    created?: string;
}

export interface ClaimCoderRuleRow extends SuppressedCoderRule {
    suppressed: boolean;
}

function toRuleNumber(value: unknown): number | undefined {
    if (typeof value === 'number') {
        return Number.isInteger(value) ? value : undefined;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value.trim());
        return Number.isInteger(parsed) ? parsed : undefined;
    }
    return undefined;
}

/** Coding notes arrive as plain text, JSON, or double-encoded JSON. */
export function formatCodingNotes(notes: unknown): string {
    if (notes === undefined || notes === null) {
        return '';
    }
    if (typeof notes !== 'string') {
        return JSON.stringify(notes, null, 2) ?? '';
    }

    const trimmed = notes.trim();
    if (trimmed === '') {
        return '';
    }

    try {
        const parsed: unknown = JSON.parse(trimmed);
        if (typeof parsed === 'string') {
            return parsed.trim();
        }
        return JSON.stringify(parsed, null, 2) ?? trimmed;
    } catch {
        return trimmed;
    }
}

function statesRuleNumber(notesText: string, ruleNumber: number): boolean {
    return new RegExp(`(?:rule\\s*[_-]?\\s*|"(?:rule)?number"\\s*:\\s*)0*${ruleNumber}(?!\\d)`, 'i').test(notesText);
}

/**
 * The rules a coding applied, read from its `Rule <n>: <text>` notes.
 *
 * `parseCodingNotes` is the reader the claim timeline and chat panel already use, so those views and
 * this one cannot disagree about which rules ran. It numbers rules positionally (1, 2, 3…) when the
 * notes state no number, which is fine for display but not here — the number is what the
 * suppression endpoints mutate, so only numbers the notes actually state are kept.
 */
export function parseCoderRulesFromNotes(notes: unknown): SuppressedCoderRule[] {
    const notesText = formatCodingNotes(notes);
    if (notesText === '') {
        return [];
    }

    const rules: SuppressedCoderRule[] = [];
    for (const rule of parseCodingNotes(notes)) {
        if (!Number.isInteger(rule.n) || !statesRuleNumber(notesText, rule.n)) {
            continue;
        }
        const description = (rule.text ?? '').trim();
        rules.push({ ruleNumber: rule.n, description: description || undefined });
    }
    return rules;
}

function codingTime(coding: CoderRuleCodingSource): number {
    const parsed = coding.created ? Date.parse(coding.created) : Number.NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Every rule the claim's codings applied, one row per rule number. A rule can be applied by several
 * codings, and the most recent coding describes it.
 */
export function extractCodingRules(codings: CoderRuleCodingSource[]): SuppressedCoderRule[] {
    const byNumber = new Map<number, SuppressedCoderRule>();
    const oldestFirst = [...codings].sort((a, b) => codingTime(a) - codingTime(b));

    for (const coding of oldestFirst) {
        for (const rule of parseCoderRulesFromNotes(coding.notes)) {
            const existing = byNumber.get(rule.ruleNumber);
            byNumber.set(rule.ruleNumber, {
                ruleNumber: rule.ruleNumber,
                description: rule.description ?? existing?.description,
            });
        }
    }

    return [...byNumber.values()].sort((a, b) => a.ruleNumber - b.ruleNumber);
}

/**
 * Claim rules in rule-number order, followed by rules the claim suppresses that no coding mentions —
 * without them a suppressed rule would drop out of future coding runs and become unrestorable.
 */
export function mergeClaimCoderRules(
    codingRules: SuppressedCoderRule[],
    suppressed: SuppressedCoderRule[],
): ClaimCoderRuleRow[] {
    const suppressedNumbers = new Set(suppressed.map(rule => rule.ruleNumber));
    const rows: ClaimCoderRuleRow[] = codingRules.map(rule => ({
        ...rule,
        suppressed: suppressedNumbers.has(rule.ruleNumber),
    }));

    const listed = new Set(codingRules.map(rule => rule.ruleNumber));
    for (const rule of suppressed) {
        if (listed.has(rule.ruleNumber)) {
            continue;
        }
        listed.add(rule.ruleNumber);
        rows.push({ ...rule, suppressed: true });
    }

    return rows;
}

export function withSuppressedRule(
    suppressed: SuppressedCoderRule[],
    rule: SuppressedCoderRule,
): SuppressedCoderRule[] {
    if (suppressed.some(current => current.ruleNumber === rule.ruleNumber)) {
        return suppressed;
    }
    return [...suppressed, { ruleNumber: rule.ruleNumber, description: rule.description }];
}

export function withoutSuppressedRule(suppressed: SuppressedCoderRule[], ruleNumber: number): SuppressedCoderRule[] {
    return suppressed.filter(current => current.ruleNumber !== ruleNumber);
}

export function parseSuppressedCoderRules(value: unknown): SuppressedCoderRule[] {
    if (!Array.isArray(value)) {
        return [];
    }

    const byNumber = new Map<number, SuppressedCoderRule>();
    for (const item of value) {
        const bare = toRuleNumber(item);
        if (bare !== undefined) {
            if (!byNumber.has(bare)) {
                byNumber.set(bare, { ruleNumber: bare });
            }
            continue;
        }
        if (item && typeof item === 'object') {
            const source = item as { ruleNumber?: unknown; description?: unknown };
            const ruleNumber = toRuleNumber(source.ruleNumber);
            if (ruleNumber === undefined) {
                continue;
            }
            const description = typeof source.description === 'string' ? source.description : undefined;
            const existing = byNumber.get(ruleNumber);
            if (!existing || (!existing.description && description)) {
                byNumber.set(ruleNumber, { ruleNumber, description });
            }
        }
    }
    return [...byNumber.values()].sort((a, b) => a.ruleNumber - b.ruleNumber);
}

/**
 * The chip counts suppressed rules, not toggles. `All on` would read as "every toggle is on" now
 * that on means suppressed — the opposite of what a zero count says.
 */
export function formatCoderRulesChipValue(suppressedCount: number | undefined): string {
    if (suppressedCount === undefined) {
        return '—';
    }
    if (suppressedCount === 0) {
        return 'None suppressed';
    }
    return `${suppressedCount} suppressed`;
}

export function summarizeCoderRuleToggles(
    ruleCount: number,
    suppressedCount: number,
): { applied: number; disabled: number } {
    const disabled = Math.min(suppressedCount, ruleCount);
    return {
        applied: Math.max(0, ruleCount - disabled),
        disabled,
    };
}

export function formatCoderRuleLabel(ruleNumber: number, description?: string, isActive?: boolean): string {
    const desc = (description ?? '').trim();
    const base = desc ? `${ruleNumber} — ${desc}` : String(ruleNumber);
    if (isActive === false) {
        return `${base} (inactive)`;
    }
    return base;
}

export function toCoderRuleSelectOptions(rules: CoderRuleOptionSource[]): CoderRuleSelectOption[] {
    return rules
        .filter((rule): rule is CoderRuleOptionSource & { ruleNumber: number } => typeof rule.ruleNumber === 'number')
        .map(rule => ({
            value: String(rule.ruleNumber),
            label: formatCoderRuleLabel(rule.ruleNumber, rule.description, rule.isActive),
            ruleNumber: rule.ruleNumber,
        }))
        .sort((a, b) => a.ruleNumber - b.ruleNumber);
}

export function parseSelectedRuleNumbers(values: Array<string | number | undefined>): number[] {
    const numbers: number[] = [];
    for (const value of values) {
        const parsed = typeof value === 'number' ? value : Number(value);
        if (Number.isInteger(parsed)) {
            numbers.push(parsed);
        }
    }
    return numbers;
}

export function expandClaimRulePairs(claimIds: string[], ruleNumbers: number[]): ClaimRulePair[] {
    const pairs: ClaimRulePair[] = [];
    for (const claimId of claimIds) {
        for (const ruleNumber of ruleNumbers) {
            pairs.push({ claimId, ruleNumber });
        }
    }
    return pairs;
}

export interface ClaimSuppressedRules {
    claimId: string;
    rules: SuppressedCoderRule[];
}

export function unionSuppressedRules(perClaim: ClaimSuppressedRules[]): SuppressedCoderRule[] {
    const byNumber = new Map<number, SuppressedCoderRule>();
    for (const { rules } of perClaim) {
        for (const rule of rules) {
            const existing = byNumber.get(rule.ruleNumber);
            if (!existing || (!existing.description && rule.description)) {
                byNumber.set(rule.ruleNumber, rule);
            }
        }
    }
    return [...byNumber.values()].sort((a, b) => a.ruleNumber - b.ruleNumber);
}

export interface SelectionCoderRule extends SuppressedCoderRule {
    /** How many of the selected claims already suppress this rule. */
    suppressedClaimCount: number;
}

/**
 * The rules a selection of claims can be told to skip: every rule their codings applied, plus every
 * rule they already suppress. The second half matters because suppression stops a rule running, so a
 * rule suppressed long enough stops appearing in coding notes altogether.
 *
 * `suppressedClaimCount` is what makes a mixed selection legible — one claim can suppress a rule the
 * next one runs, and a batch action has to say so rather than pick a side silently.
 */
export function summarizeSelectionRules(
    codingRules: SuppressedCoderRule[],
    perClaim: ClaimSuppressedRules[],
): SelectionCoderRule[] {
    const suppressedCounts = new Map<number, number>();
    const suppressedDescriptions = new Map<number, string | undefined>();
    for (const { rules } of perClaim) {
        for (const rule of rules) {
            suppressedCounts.set(rule.ruleNumber, (suppressedCounts.get(rule.ruleNumber) ?? 0) + 1);
            if (rule.description && !suppressedDescriptions.get(rule.ruleNumber)) {
                suppressedDescriptions.set(rule.ruleNumber, rule.description);
            }
        }
    }

    const byNumber = new Map<number, SelectionCoderRule>();
    for (const rule of codingRules) {
        byNumber.set(rule.ruleNumber, {
            ruleNumber: rule.ruleNumber,
            description: rule.description ?? suppressedDescriptions.get(rule.ruleNumber),
            suppressedClaimCount: suppressedCounts.get(rule.ruleNumber) ?? 0,
        });
    }
    for (const [ruleNumber, count] of suppressedCounts) {
        if (byNumber.has(ruleNumber)) {
            continue;
        }
        byNumber.set(ruleNumber, {
            ruleNumber,
            description: suppressedDescriptions.get(ruleNumber),
            suppressedClaimCount: count,
        });
    }

    return [...byNumber.values()].sort((a, b) => a.ruleNumber - b.ruleNumber);
}

export function formatSelectionRuleLabel(rule: SelectionCoderRule, claimCount: number): string {
    const base = formatCoderRuleLabel(rule.ruleNumber, rule.description);
    if (rule.suppressedClaimCount === 0) {
        return base;
    }
    if (rule.suppressedClaimCount >= claimCount) {
        return `${base} (suppressed on all ${claimCount})`;
    }
    return `${base} (suppressed on ${rule.suppressedClaimCount} of ${claimCount})`;
}

export function toSelectionRuleSelectOptions(rules: SelectionCoderRule[], claimCount: number): CoderRuleSelectOption[] {
    return rules.map(rule => ({
        value: String(rule.ruleNumber),
        label: formatSelectionRuleLabel(rule, claimCount),
        ruleNumber: rule.ruleNumber,
    }));
}

/**
 * Claim/rule pairs to suppress, skipping claims that already suppress the rule — a selection is
 * routinely mixed, and re-writing a rule a claim already skips is a request that can only fail or
 * do nothing.
 */
export function expandSuppressPairs(perClaim: ClaimSuppressedRules[], ruleNumbers: number[]): ClaimRulePair[] {
    const pairs: ClaimRulePair[] = [];
    for (const { claimId, rules } of perClaim) {
        const alreadySuppressed = new Set(rules.map(rule => rule.ruleNumber));
        for (const ruleNumber of ruleNumbers) {
            if (alreadySuppressed.has(ruleNumber)) {
                continue;
            }
            pairs.push({ claimId, ruleNumber });
        }
    }
    return pairs;
}

export function expandRestorePairs(perClaim: ClaimSuppressedRules[], ruleNumbers: number[]): ClaimRulePair[] {
    const selected = new Set(ruleNumbers);
    const pairs: ClaimRulePair[] = [];
    for (const { claimId, rules } of perClaim) {
        for (const rule of rules) {
            if (selected.has(rule.ruleNumber)) {
                pairs.push({ claimId, ruleNumber: rule.ruleNumber });
            }
        }
    }
    return pairs;
}

export function partitionPairResults(results: PairAttemptResult[]): {
    succeeded: PairAttemptResult[];
    failed: PairAttemptResult[];
} {
    return {
        succeeded: results.filter(result => result.ok),
        failed: results.filter(result => !result.ok),
    };
}

export function pairAttemptErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return String(error);
}

export async function runClaimRulePairs(
    pairs: ClaimRulePair[],
    run: (claimId: string, ruleNumber: number) => Promise<void>,
    concurrency = 5,
    onProgress?: (completed: number, total: number) => void,
): Promise<PairAttemptResult[]> {
    return mapWithConcurrency(
        pairs,
        concurrency,
        async pair => {
            try {
                await run(pair.claimId, pair.ruleNumber);
                return { pair, ok: true };
            } catch (error) {
                return { pair, ok: false, error: pairAttemptErrorMessage(error) };
            }
        },
        onProgress,
    );
}

export async function suppressClaimRulePairs(
    pairs: ClaimRulePair[],
    suppress: (claimId: string, ruleNumber: number) => Promise<void>,
    concurrency = 5,
): Promise<PairAttemptResult[]> {
    return runClaimRulePairs(pairs, suppress, concurrency);
}
